/**
 * Converts hex color to HSL values
 * @param {string} hex - Hex color string (e.g., "#FF0000" or "#FFF")
 * @returns {Object} HSL values { h, s, l }
 * @throws {Error} If hex string is invalid
 */
export const hexToHSL = (hex) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Validate hex string (3 or 6 characters)
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  // Expand short-form hex (#FFF -> #FFFFFF)
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
      h = ((b - r) / delta + 2) / 6;
    } else {
      h = ((r - g) / delta + 4) / 6;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Returns a contrasting text color ('#000000' or '#ffffff') for a given hex background.
 * Uses the W3C relative luminance formula for accessibility.
 * @param {string} hex - Hex color string (e.g., "#6B8E6B")
 * @returns {string} '#000000' for light backgrounds, '#ffffff' for dark backgrounds
 */
export function contrastTextColor(hex) {
  if (!hex) return '#ffffff';
  let normalized = hex.trim();
  // Expand short-form hex (#FFF → #FFFFFF)
  if (/^#[0-9A-Fa-f]{3}$/.test(normalized)) {
    normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
  }
  if (normalized.length < 7) return '#ffffff';
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}
